import { useState, type FC } from 'react'
import cx from 'classnames'
import { twMerge } from 'tailwind-merge'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'
import { getValueFormatter, type ValueFormat } from '@egw/utils'
import { type DefaultProps } from '../common'

const COLORS = [
  '#7810F8',
  '#FB21FF',
  '#0088FF',
  '#F26B2B',
  '#0700DF',
  '#46A800',
  '#DC0385',
  '#BA27F3',
  '#1DE3A1',
  '#ACD711',
  '#05BDF4',
  '#FCC505',
  '#EA400D',
  '#20D5D5',
  '#FF009D',
]

// Padding to account for stroke width (strokeWidth={1} needs ~2px on each side)
const STROKE_PADDING = 2

export type DonutChartData = Record<string, string | number>

export interface DonutChartProps extends DefaultProps {
  /**
   * Data for the donut chart
   */
  data: DonutChartData[]
  /**
   * Title displayed in the center of the donut
   */
  title?: string
  /**
   * Description
   */
  description?: string
  /**
   * Inner radius ('60%' or 160)
   */
  innerRadius?: number | string
  /**
   * Outer radius ('100%' or 326)
   */
  outerRadius?: number | string
  /**
   * Click handler for chart segments
   */
  onSegmentClick?: (_data: DonutChartData, _index: number) => void
  /**
   * Click handler for the container
   */
  onClick?: (_data: DonutChartData) => void
  /**
   * Show legend below the chart
   */
  showLegend?: boolean
  /**
   * Title className
   */
  titleClassName?: string
  /**
   * Value format
   */
  valueFormat?: ValueFormat
}

export const DonutChart: FC<DonutChartProps> = ({
  data,
  title,
  description,
  innerRadius = 80,
  outerRadius = 163,
  onSegmentClick,
  onClick,
  showLegend = true,
  className,
  id,
  testId,
  ariaLabel,
  titleClassName,
  valueFormat = 'number',
}) => {
  const [activeSegment, setActiveSegment] = useState<DonutChartData | null>(null)

  // Auto-detect keys from data
  const nameKey = data.length > 0 ? Object.keys(data[0])[0] : 'name'
  const valueKey =
    data.length > 0
      ? Object.keys(data[0]).find((key) => typeof data[0][key] === 'number') || Object.keys(data[0])[1] || 'value'
      : 'value'

  // Component base class names
  const baseClass = cx('flex flex-col items-center')

  // Final class names
  const finalClasses = twMerge(baseClass, className)

  const handleSegmentClick = (_data: DonutChartData, _index: number): void => {
    onSegmentClick?.(_data, _index)
    const currentName = String(activeSegment?.[nameKey] ?? '')
    const newName = String(_data[nameKey] ?? '')
    if (currentName === newName) {
      setActiveSegment(null)
    } else {
      setActiveSegment(_data)
    }
  }

  return (
    <div
      className={finalClasses}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    >
      <div className={cx('flex outline-none flex-col items-center w-full h-full')}>
        <p className={cx('typo-primary-2xl-light text-text-header', titleClassName)}>{title}</p>
        {description ? <p className={cx('typo-primary-base-regular text-text-body mt-3xs')}>{description}</p> : null}
        <div
          className={cx('relative w-full h-full flex justify-center items-center', {
            'mt-lg': description ?? title,
          })}
          style={outerRadius === '100%' ? { aspectRatio: 1 } : {}}
        >
          <ResponsiveContainer
            width={
              outerRadius && typeof outerRadius === 'number'
                ? outerRadius * 2 + STROKE_PADDING
                : (outerRadius as `${number}%`)
            }
            height={
              outerRadius && typeof outerRadius === 'number'
                ? outerRadius * 2 + STROKE_PADDING
                : (outerRadius as `${number}%`)
            }
            aspect={1}
            minHeight={326 + STROKE_PADDING}
            minWidth={326 + STROKE_PADDING}
          >
            <PieChart>
              <Tooltip
                isAnimationActive={false}
                content={<DonutChartTooltip valueFormat={valueFormat} nameKey={nameKey} valueKey={valueKey} />}
              />
              <Pie
                data={data as (DonutChartData & Record<string, unknown>)[]}
                cx="50%"
                cy="50%"
                innerRadius={innerRadius}
                outerRadius={outerRadius}
                paddingAngle={2}
                cornerRadius={2}
                dataKey={valueKey}
                startAngle={90}
                endAngle={-270}
              >
                {data.map((entry, index) => {
                  const entryName = String(entry[nameKey] ?? '')
                  const activeName = String(activeSegment?.[nameKey] ?? '')
                  return (
                    <Cell
                      key={`cell-${entryName}`}
                      fill={COLORS[index % COLORS.length]}
                      fillOpacity={activeName === entryName ? 1 : 0.1}
                      stroke={COLORS[index % COLORS.length]}
                      strokeWidth={1}
                      onClick={() => {
                        handleSegmentClick(entry, index)
                        onClick?.(entry)
                      }}
                      data-testid={`donut-segment-${entryName}`}
                      aria-label={`${entryName}: ${String(entry[valueKey])}`}
                      className="cursor-pointer active:opacity-100 outline-none hover:opacity-80"
                    />
                  )
                })}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>

        {Boolean(showLegend) && (
          <div
            className={cx('grid gap-y-2xs gap-x-lg w-full mt-lg   grid-flow-row md:grid-flow-col', {
              'grid-cols-1 md:grid-cols-1': data.length === 1,
              'grid-cols-1 md:grid-cols-2': data.length > 1,
            })}
            style={{
              gridTemplateRows: `repeat(${data.length > 1 ? Math.ceil(data.length / 2).toString() : '1'}, 1fr)`,
              width: 'fit-content',
            }}
          >
            {data.map((entry, index) => {
              const entryName = String(entry[nameKey] ?? '')
              const activeName = String(activeSegment?.[nameKey] ?? '')
              return (
                <div
                  key={`legend-${entryName}`}
                  className="flex items-center gap-2xs cursor-pointer hover:opacity-80"
                  onClick={() => {
                    handleSegmentClick(entry, index)
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleSegmentClick(entry, index)
                    }
                  }}
                >
                  <div
                    className="relative w-3 h-3 rounded-sm flex-shrink-0 shadow-[0_0_0_1px_#000]"
                    style={{ boxShadow: `0 0 0 1px ${COLORS[index % COLORS.length]}` }}
                  >
                    {/* Background with opacity */}
                    <div
                      className={cx(`absolute inset-0 rounded-sm`, {
                        'opacity-100': activeName === entryName,
                        'opacity-20': activeName !== entryName,
                      })}
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                  </div>
                  <span
                    className={cx('text-text-body truncate min-w-[220px]', {
                      'typo-primary-base-semibold': activeName === entryName,
                      'typo-primary-base-regular': activeName !== entryName,
                    })}
                  >
                    {entryName}
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// Custom Tooltip component for donut chart
const DonutChartTooltip = ({
  active,
  payload,
  valueFormat,
  nameKey,
  valueKey,
}: {
  active?: boolean
  payload?: { payload?: DonutChartData }[]
  valueFormat?: ValueFormat
  nameKey: string
  valueKey: string
}): JSX.Element | null => {
  if (!active || !payload || payload.length === 0) {
    return null
  }

  const segmentData = payload[0]?.payload
  if (!segmentData) {
    return null
  }

  const name = String(segmentData[nameKey] ?? '')
  const value = Number(segmentData[valueKey] ?? 0)
  const formatValue = getValueFormatter(valueFormat)

  return (
    <div className="flex flex-col bg-white border border-borders-light rounded-[12px] p-2xs shadow-lg gap-1">
      <p className="typo-primary-base-regular text-text-header">{name}</p>
      <p className="typo-primary-base-regular text-text-header">{formatValue(value)}</p>
    </div>
  )
}
